import { useEffect, useId, useRef, useState } from "react";
import { BarChart3, Check, ChevronDown, Plus } from "lucide-react";
import { useOptionalPrivanaI18n } from "@/i18n/PrivanaI18nProvider";
import { useMemberPortalApi } from "../api/memberPortalApiClient";
import "./golfPersonalStats.css";
export function useGolfStatsText() {
  const { t, locale } = useOptionalPrivanaI18n();
  return { t: (key, values) => t(`golfStats.${key}`, values), locale };
}
export function GolfStatLine({ stat, par }) {
  const { t } = useGolfStatsText();
  const answer = (v) => (v == null ? "—" : v ? t("yes") : t("no"));
  return (
    <small className="mp-personal-stat-line">
      {t("putts")}: {stat?.putts ?? "—"} · {t("fairway")}:{" "}
      {![4, 5].includes(par)
        ? t("na")
        : stat?.fairway_hit == null
          ? "—"
          : stat.fairway_hit
            ? t("hit")
            : t("missed")}{" "}
      · {t("gir")}: {par == null ? t("na") : answer(stat?.gir)}
    </small>
  );
}
// Each entry keeps acknowledged values, an unsaved patch and (when needed) a
// separate review snapshot. A transport retry never rebases a patch implicitly.
export function usePersonalGolfStats({ data, clubCode, roundId, setSync }) {
  const { memberPortalApiPatch, memberPortalApiGet } = useMemberPortalApi();
  const [state, setState] = useState({
    owner: null,
    stats: {},
    errors: {},
    conflicts: {},
    busy: {},
  });
  const entries = useRef({}),
    epoch = useRef(0),
    attached = useRef(false);
  const nextScope = `${clubCode}:${roundId}:${data?.myParticipantId}`;
  const renderedScope = useRef(nextScope);
  renderedScope.current = nextScope;
  const empty = () => ({
    putts: null,
    fairway_hit: null,
    gir: null,
    revision: 0,
  });
  const fields = ["putts", "fairway_hit", "gir"];
  const live = (generation, owner) =>
    attached.current &&
    generation === epoch.current &&
    owner === renderedScope.current;
  const draftOf = (entry) => ({
    ...(entry.latest || entry.saved),
    ...entry.patch,
  });
  const publish = () => {
    const current = Object.entries(entries.current);
    setState({
      owner: renderedScope.current,
      stats: Object.fromEntries(
        current.map(([number, e]) => [number, draftOf(e)]),
      ),
      errors: Object.fromEntries(
        current
          .filter(([, e]) => e.error)
          .map(([number, e]) => [number, e.error]),
      ),
      conflicts: Object.fromEntries(
        current
          .filter(([, e]) => e.latest)
          .map(([number, e]) => [
            number,
            {
              latest: e.latest,
              draft: draftOf(e),
              fields: Object.keys(e.patch),
            },
          ]),
      ),
      busy: Object.fromEntries(
        current.map(([number, e]) => [number, e.running || e.reconciling]),
      ),
    });
    const blocked = current.some(([, e]) => e.error || e.latest);
    const pending = current.some(
      ([, e]) => e.running || e.reconciling || Object.keys(e.patch).length,
    );
    setSync(blocked ? "Unsaved" : pending ? "Saving…" : "Saved");
  };
  useEffect(() => {
    epoch.current++;
    attached.current = true;
    entries.current = {};
    for (const s of data?.personalStats || [])
      entries.current[s.hole_number] = {
        saved: s,
        patch: {},
        running: false,
        reconciling: false,
        error: null,
        latest: null,
      };
    publish();
    return () => {
      attached.current = false;
      epoch.current++;
    };
  }, [nextScope]);

  const loadLatest = async (number, entry, forceReview = false) => {
    const generation = epoch.current,
      owner = nextScope;
    if (!live(generation, owner) || entries.current[number] !== entry)
      return "stale";
    entry.reconciling = true;
    publish();
    try {
      const response = await memberPortalApiGet(
        `/api/members-portal/${clubCode}/lets-play/golf/rounds/${roundId}`,
      );
      const payload = await response.json();
      if (!live(generation, owner)) return "stale";
      if (
        !response.ok ||
        payload.data?.myParticipantId !== data?.myParticipantId
      )
        throw Error("stats_save_failed");
      const latest =
        payload.data.personalStats.find(
          (s) =>
            s.hole_number === number &&
            s.participant_id === data.myParticipantId,
        ) || empty();
      if (forceReview) {
        entry.latest = latest;
        entry.error = null;
        return "review";
      }
      // A lost response may already have committed the desired values. Accept
      // them without another mutation, even if unrelated fields also advanced.
      if (
        Object.entries(entry.patch).every(
          ([key, value]) => latest[key] === value,
        )
      ) {
        entry.saved = latest;
        entry.patch = {};
        entry.latest = null;
        entry.error = null;
        return "satisfied";
      }
      if (latest.revision !== entry.saved.revision) {
        entry.latest = latest;
        entry.error = null;
        return "review";
      }
      if (!payload.data.permissions?.canRecordStats)
        throw Error("round_not_editable");
      // The expected version has NOT changed. Retrying this exact version is
      // safe: a still-running original request can only cause a CAS conflict.
      entry.error = null;
      return "retry";
    } catch (error) {
      if (live(generation, owner))
        entry.error = error.message || "stats_save_failed";
      return "failed";
    } finally {
      if (live(generation, owner)) {
        entry.reconciling = false;
        publish();
      }
    }
  };
  const save = async (number, entry) => {
    if (
      !live(epoch.current, nextScope) ||
      entries.current[number] !== entry ||
      entry.running ||
      entry.reconciling ||
      entry.error ||
      entry.latest ||
      !Object.keys(entry.patch).length
    )
      return;
    const generation = epoch.current,
      owner = nextScope;
    entry.running = true;
    publish();
    try {
      while (Object.keys(entry.patch).length) {
        const patch = { ...entry.patch },
          revision = entry.saved.revision;
        const response = await memberPortalApiPatch(
          `/api/members-portal/${clubCode}/lets-play/golf/rounds/${roundId}/my-stats/${number}`,
          { ...patch, revision },
        );
        const payload = await response.json();
        if (!live(generation, owner)) return;
        if (!response.ok) {
          if (payload.error === "stats_changed_reload") {
            entry.error = "stats_changed_reload";
            entry.mustReview = true;
            await loadLatest(number, entry, true);
            return;
          }
          throw Error(payload.error || "stats_save_failed");
        }
        if (
          payload.data?.participant_id !== data.myParticipantId ||
          payload.data?.hole_number !== number ||
          (payload.data.club_code && payload.data.club_code !== clubCode) ||
          (payload.data.activity_id && payload.data.activity_id !== roundId)
        )
          throw Error("stats_save_failed");
        entry.saved = payload.data;
        for (const [key, value] of Object.entries(patch))
          if (entry.patch[key] === value) delete entry.patch[key];
        publish();
      }
    } catch (error) {
      if (live(generation, owner))
        entry.error = error.message || "stats_save_failed";
    } finally {
      if (live(generation, owner)) {
        entry.running = false;
        publish();
      }
    }
  };
  const update = (number, patch) => {
    if (!live(epoch.current, nextScope) || !data?.permissions?.canRecordStats)
      return;
    const entry = (entries.current[number] ||= {
      saved: empty(),
      patch: {},
      running: false,
      reconciling: false,
      error: null,
      latest: null,
    });
    for (const key of fields)
      if (Object.hasOwn(patch, key)) entry.patch[key] = patch[key];
    publish();
    // Editing a blocked draft is local only. Neither an edit nor Retry is
    // consent to overwrite a newer version.
    save(number, entry);
  };
  const retry = async (number) => {
    if (!live(epoch.current, nextScope)) return;
    const entry = entries.current[number];
    if (!entry || entry.running || entry.reconciling || entry.latest) return;
    const generation = epoch.current;
    const result = await loadLatest(number, entry, entry.mustReview === true);
    if (live(generation, nextScope) && result === "retry") save(number, entry);
  };
  const useLatest = (number) => {
    if (!live(epoch.current, nextScope)) return;
    const entry = entries.current[number];
    if (!entry?.latest || entry.running || entry.reconciling) return;
    entry.saved = entry.latest;
    entry.latest = null;
    entry.patch = {};
    entry.error = null;
    entry.mustReview = false;
    publish();
  };
  const confirm = (number) => {
    if (!live(epoch.current, nextScope)) return;
    const entry = entries.current[number];
    if (
      !entry?.latest ||
      entry.running ||
      entry.reconciling ||
      !data?.permissions?.canRecordStats
    )
      return;
    // Confirmation is tied to the values actually displayed for review. A
    // subsequent writer produces a new conflict; never refresh the version here.
    entry.saved = entry.latest;
    entry.latest = null;
    entry.error = null;
    entry.mustReview = false;
    for (const key of Object.keys(entry.patch))
      if (entry.patch[key] === entry.saved[key]) delete entry.patch[key];
    publish();
    save(number, entry);
  };
  // Do not expose the previous golfer's values during the render before the
  // new scope's effect initialises its entries.
  const visible =
    state.owner === nextScope
      ? state
      : { stats: {}, errors: {}, conflicts: {}, busy: {} };
  return {
    stats: visible.stats,
    errors: visible.errors,
    conflicts: visible.conflicts,
    busy: visible.busy,
    update,
    retry,
    useLatest,
    confirm,
  };
}
export default function GolfPersonalStats({
  active = true,
  hole,
  stat,
  onChange,
  canEdit,
  expanded,
  setExpanded,
  failed,
  retry,
  conflict,
  busy,
  useLatest,
  confirm,
}) {
  const { t } = useGolfStatsText();
  const helpId=useId();
  const [editing, setEditing] = useState(false);
  const [exact, setExact] = useState("");
  const [invalid, setInvalid] = useState(false);
  const customButton = useRef(null),
    exactInput = useRef(null);
  useEffect(() => {
    setEditing(false);
    setInvalid(false);
  }, [hole.hole_number, expanded, active]);
  useEffect(() => {
    if (editing) {
      exactInput.current?.focus();
      exactInput.current?.select();
    }
  }, [editing]);
  const closeEditor = () => {
    setEditing(false);
    setInvalid(false);
    customButton.current?.focus();
  };
  const applyExact = () => {
    if (!/^\d+$/.test(exact) || Number(exact) > 30) {
      setInvalid(true);
      exactInput.current?.focus();
      return;
    }
    onChange({ putts: Number(exact) });
    closeEditor();
  };
  const choices = (field, yes, no) => (
    <div className="mp-stats-choices" role="group" aria-label={t(field === "gir" ? "gir" : "fairway")}>
      {[
        [true, yes],
        [false, no],
      ].map(([value, label]) => (
        <button
          type="button"
          key={label}
          disabled={!canEdit}
          aria-pressed={stat?.[field] === value}
          onClick={() =>
            onChange({ [field]: stat?.[field] === value ? null : value })
          }
        >
          {stat?.[field] === value && <Check size={15} />} {label}
        </button>
      ))}
    </div>
  );
  return (
    <section className="mp-personal-stats" aria-label={t("title")}>
      <button
        type="button"
        disabled={!active}
        className="mp-personal-stats__heading"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <BarChart3 size={18} />
        <strong>{t("title")}</strong>
        <small>{t("optional")}</small>
        <ChevronDown size={18} />
      </button>
      {expanded && (
        <div className="mp-personal-stats__body">
          <div className="mp-stats-putts">
            <span>{t("putts")}</span>
            <div
              className="mp-stats-putt-options"
              role="group"
              aria-label={t("putts")}
            >
              {[0, 1, 2, 3, 4].map((value) => (
                <button
                  type="button"
                  key={value}
                  disabled={!canEdit}
                  aria-label={t("puttChoice", { count: value })}
                  aria-pressed={stat?.putts === value}
                  onClick={() =>
                    onChange({ putts: stat?.putts === value ? null : value })
                  }
                >
                  {value}
                </button>
              ))}
              <button
                type="button"
                ref={customButton}
                disabled={!canEdit}
                aria-label={t("exactPutts")}
                aria-expanded={editing}
                aria-pressed={stat?.putts > 4}
                onClick={() => {
                  setExact(String(stat?.putts ?? 5));
                  setEditing(true);
                }}
              >
                {stat?.putts > 4 ? stat.putts : <Plus size={18} />}
              </button>
            </div>
            {editing && (
              <div
                className="mp-stats-exact"
                role="group"
                aria-label={t("exactPutts")}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    event.preventDefault();
                    closeEditor();
                  }
                }}
              >
                <label>
                  {t("exactPutts")}
                  <input
                    ref={exactInput}
                    disabled={!canEdit}
                    type="number"
                    inputMode="numeric"
                    min="0"
                    max="30"
                    value={exact}
                    aria-invalid={invalid}
                    aria-describedby={invalid ? `${helpId}-putts-error` : undefined}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        applyExact();
                      }
                    }}
                    onChange={(event) => {
                      setExact(event.target.value);
                      setInvalid(false);
                    }}
                  />
                </label>
                <button type="button" disabled={!canEdit} onClick={applyExact}>
                  {t("apply")}
                </button>
                <button type="button" onClick={closeEditor}>
                  {t("cancel")}
                </button>
                {invalid && (
                  <small id={`${helpId}-putts-error`} role="alert">{t("errors.invalid_putts")}</small>
                )}
              </div>
            )}
          </div>
          <div className="mp-stats-binary">
            {hole.statsPar !== 3 && (
              <div className="mp-stats-row">
                <span>{t("fairway")}</span>
                {[4, 5].includes(hole.statsPar) ? (
                  choices("fairway_hit", t("hit"), t("missed"))
                ) : (
                  <small>{t("unknownPar")}</small>
                )}
              </div>
            )}
            <div className="mp-stats-row">
              <span role="img" aria-label={t("gir")}>GIR</span>
              {hole.statsPar != null ? (
                choices("gir", t("yes"), t("no"))
              ) : (
                <small>{t("unknownPar")}</small>
              )}
            </div>
          </div>
          <button
            type="button"
            className="mp-stats-clear"
            disabled={!canEdit}
            onClick={() =>
              onChange({ putts: null, fairway_hit: null, gir: null })
            }
          >
            {t("clearAll")}
          </button>
          {conflict && (
            <div className="mp-stats-conflict" role="alert" aria-live="polite">
              <strong>{t("conflictTitle")}</strong>
              <p>{t("conflictHelp")}</p>
              <div>
                <span>{t("latestSaved")}</span>
                <GolfStatLine stat={conflict.latest} par={hole.statsPar} />
              </div>
              <div>
                <span>{t("yourDraft")}</span>
                <GolfStatLine stat={conflict.draft} par={hole.statsPar} />
              </div>
              <p>{t("editedFieldsOnly")}</p>
              <div className="mp-stats-conflict__actions">
                <button
                  type="button"
                  disabled={!active || busy}
                  onClick={useLatest}
                >
                  {t("useLatest")}
                </button>
                <button
                  type="button"
                  disabled={!canEdit || busy}
                  onClick={confirm}
                >
                  {t("confirmReviewed")}
                </button>
              </div>
            </div>
          )}
          {failed && !conflict && (
            <p role="alert">
              {t(`errors.${failed}`).startsWith("[missing:")
                ? t("saveError")
                : t(`errors.${failed}`)}{" "}
              <button type="button" disabled={!active || busy} onClick={retry}>
                {busy ? t("checkingLatest") : t("retry")}
              </button>
            </p>
          )}
        </div>
      )}
    </section>
  );
}
